import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Select, Button, Input, Spin, message } from 'antd';
import { LeftOutlined, InfoCircleOutlined, AppstoreOutlined } from '@ant-design/icons';
import api from '../api';
import { H5_TAB_BAR_PX } from '../h5Layout';

export default function ApplyChange() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [newLocationId, setNewLocationId] = useState(null);
  const [newUserId, setNewUserId] = useState(null);
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/assets/${id}`),
      api.get('/asset-locations', { params: { status: 1 } }),
      api.get('/asset-users', { params: { status: 1 } }),
    ]).then(([assetRes, locRes, userRes]) => {
      setAsset(assetRes);
      const locList = Array.isArray(locRes) ? locRes : (locRes?.list || []);
      setLocations(locList);
      const ulist = Array.isArray(userRes) ? userRes : (userRes?.list || []);
      setUsers(ulist);
    }).catch(() => message.error('加载失败'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async () => {
    if (!newLocationId && !newUserId) { message.warning('请选择变更内容'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/applies', {
        apply_type: 2,
        asset_ids: [Number(id)],
        new_location_id: newLocationId,
        new_user_id: newUserId,
        remark,
      });
      if (res?.direct_done) {
        message.success('变更办理成功（无需审核）');
      } else {
        message.success('变更申请已提交：待新位置区域管理员审核（已抄送超管与资产负责人）');
      }
      navigate(-1);
    } catch (err) {
      message.error(err.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const sectionTitle = (text) => (
    <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 12, paddingLeft: 8, borderLeft: '3px solid #1677ff' }}>{text}</div>
  );

  const formatLocationLabel = (location) => {
    const locationName = String(location?.location_name || '').trim();
    const regionName = String(location?.region_name || '').trim();
    const hasValidRegion = regionName && regionName !== '未分配区域';
    return `${locationName}${hasValidRegion ? `（${regionName}）` : ''}`.trim() || String(location?.id || '');
  };

  const infoRow = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
      <span style={{ fontSize: 14, color: '#999' }}>{label}</span>
      <span style={{ fontSize: 14, color: '#333' }}>{value || '-'}</span>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f7fa',
      paddingBottom: `calc(${H5_TAB_BAR_PX}px + 88px + env(safe-area-inset-bottom, 0px))`,
    }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <LeftOutlined onClick={() => navigate(-1)} style={{ fontSize: 18, marginRight: 12 }} />
        <span style={{ fontSize: 17, fontWeight: 600 }}>申请变更</span>
      </div>

      <Spin spinning={loading}>
        {asset && (
          <div style={{ padding: 16 }}>
            {/* Asset info card */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: '#e6f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AppstoreOutlined style={{ fontSize: 22, color: '#1677ff' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#333' }}>{asset.asset_name}</div>
                  <div style={{ fontSize: 13, color: '#999', marginTop: 2 }}>{asset.asset_code}</div>
                </div>
                <div style={{ fontSize: 12, color: '#1677ff', background: '#e6f4ff', padding: '2px 8px', borderRadius: 4 }}>
                  当前状态: 使用中
                </div>
              </div>
            </div>

            {/* Current info section */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              {sectionTitle('当前信息')}
              {infoRow('原使用人', asset.user_name || '-')}
              {infoRow('原存放位置', asset.location_name)}
            </div>

            {/* Change to section */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              {sectionTitle('变更为')}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, color: '#666', marginBottom: 6 }}>新使用人</div>
                <Select
                  placeholder="请选择使用人"
                  style={{ width: '100%' }}
                  allowClear
                  value={newUserId}
                  onChange={setNewUserId}
                  showSearch
                  optionFilterProp="label"
                  options={users.map(u => ({ value: u.id, label: u.username || '' }))}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, color: '#666', marginBottom: 6 }}>新存放位置</div>
                <Select
                  placeholder="请选择存放位置"
                  style={{ width: '100%' }}
                  allowClear
                  value={newLocationId}
                  onChange={setNewLocationId}
                  showSearch
                  optionFilterProp="label"
                  options={locations.map((l) => ({
                    value: l.id,
                    label: formatLocationLabel(l),
                  }))}
                />
              </div>
              <div>
                <div style={{ fontSize: 14, color: '#666', marginBottom: 6 }}>变更原因（选填）</div>
                <Input.TextArea
                  rows={3}
                  placeholder="请输入变更原因"
                  value={remark}
                  onChange={e => setRemark(e.target.value)}
                  style={{ borderRadius: 8 }}
                />
              </div>
            </div>

            {/* Info tip */}
            <div style={{ background: '#e6f4ff', borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
              <InfoCircleOutlined style={{ color: '#1677ff', marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#1677ff', lineHeight: 1.5 }}>
                变更存放地点后，由「新位置」所属区域管理员审核；系统会抄送超级管理员与资产负责人。仅改使用人时，区域管理员对本管辖资产可直接办理。
              </span>
            </div>
          </div>
        )}
      </Spin>

      {/* Submit button */}
      <div style={{
        position: 'fixed',
        bottom: H5_TAB_BAR_PX,
        left: 0,
        right: 0,
        padding: '12px 16px',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
        background: '#fff',
        borderTop: '1px solid #f0f0f0',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
        zIndex: 90,
      }}
      >
        <Button
          type="primary"
          block
          size="large"
          loading={submitting}
          onClick={handleSubmit}
          style={{ borderRadius: 8, height: 46, fontSize: 16, fontWeight: 500 }}
        >
          提交变更申请
        </Button>
      </div>
    </div>
  );
}
